import { openDB } from "idb";
import { BASE_URL, CHECK_IN } from "../constants";

const DB_NAME = "crm-offline-db";
const DB_VERSION = 2;
const STORE_NAME = "pending-submissions";

class OfflineStorage {
  constructor() {
    this.db = null;
    this.initDB();
  }

  async initDB() {
    this.db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, {
            keyPath: "id",
            autoIncrement: true,
          });
          store.createIndex("type", "type");
          store.createIndex("status", "status");
          store.createIndex("timestamp", "timestamp");
        }
      },
    });
  }

  // Save form data to IndexedDB
  async saveFormData(type, formData, images = []) {
    if (!this.db) await this.initDB();

    // Convert FormData to regular object
    const formDataObj = {};
    formData.forEach((value, key) => {
      formDataObj[key] = value;
    });

    // Convert images to base64
    const processedImages = await Promise.all(
      images.map(async (image) => {
        if (image instanceof File) {
          return {
            name: image.name,
            type: image.type,
            data: await this.fileToBase64(image),
          };
        }
        return image;
      })
    );

    const submission = {
      type, // 'meeting', 'site_visit', or 'estimate'
      formData: formDataObj,
      images: processedImages,
      status: "pending",
      timestamp: new Date().toISOString(),
      retryCount: 0,
    };



    const id = await this.db.add(STORE_NAME, submission);

    // Start correct sync service
    if (type === "check_in") {
      this.startCheckInSyncService();
    } else {
      this.startSubmissionSyncService();
    }

    return { id, ...submission };
  }

  // Convert File to base64
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  startSubmissionSyncService() {
    if (!this.submissionSyncStarted) {
      this.submissionSyncStarted = true;
      setInterval(() => this.syncPendingSubmissions(), 60000);
      window.addEventListener("online", () => this.syncPendingSubmissions());
    }
  }


  // Save form data to IndexedDB
  async saveCheckInData(type, formData) {
    if (!this.db) await this.initDB();


    const submission = {
      type, // 'meeting', 'site_visit', or 'estimate', 'check_in'
      formData: formData,
      status: "pending",
      timestamp: "",
      checkInTime: new Date().toISOString(),
      retryCount: 0,
    };

    const id = await this.db.add(STORE_NAME, submission);

    // Start Check-In Sync Service only
    this.startCheckInSyncService();

    return { id, ...submission };
  }


  startCheckInSyncService() {
    if (!this.checkInSyncStarted) {
      this.checkInSyncStarted = true;
      setInterval(() => this.syncPendingSubmissions1(), 60000);
      window.addEventListener("online", () => this.syncPendingSubmissions1());
    }
  }

  async syncPendingSubmissions() {
    if (!navigator.onLine) return;

    try {
      // First get all pending submissions in one transaction
      const tx1 = this.db.transaction(STORE_NAME, "readonly");
      const store1 = tx1.objectStore(STORE_NAME);
      const pending = await store1.index("status").getAll("pending");
      await tx1.done; // Wait for first transaction to complete

      // Process each submission
      for (const submission of pending) {
        try {
          const formData = new FormData();
          
          
          // Add form data
          Object.entries(submission.formData).forEach(([key, value]) => {
            formData.append(key, value);
            
          });

       

          // Handle images
          if (submission.images && submission.images.length > 0) {
            for (const image of submission.images) {
              const file = await this.base64ToFile(
                image.data,
                image.name,
                image.type
              );
              formData.append("images", file);
            }
          }

       

          const endpoint = this.getEndpoint(submission.type);

     
          // Try to submit to backend
          const response = await fetch(endpoint, {
            method: "POST",
            body: formData,
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          });

          console.log("response", response);

          if (response.ok) {
            // Create new transaction for deletion
            const deleteTx = this.db.transaction(STORE_NAME, "readwrite");
            const deleteStore = deleteTx.objectStore(STORE_NAME);
            await deleteStore.delete(submission.id);
            await deleteTx.done; // Wait for deletion to complete

            this.notifySuccess(submission.type);
          } else {
            throw new Error("API submission failed");
          }
        } catch (error) {
          console.error("Sync error:", error);

          // Create new transaction for update
          const updateTx = this.db.transaction(STORE_NAME, "readwrite");
          const updateStore = updateTx.objectStore(STORE_NAME);

          const updatedSubmission = {
            ...submission,
            retryCount: (submission.retryCount || 0) + 1,
            status: submission.retryCount >= 2 ? "failed" : "pending",
          };

          await updateStore.put(updatedSubmission);
          await updateTx.done; // Wait for update to complete
        }
      }
    } catch (error) {
      console.error("Sync process error:", error);
    }
  }

  async syncPendingSubmissions1() {
    if (!navigator.onLine) return;

    try {
      // First get all pending submissions in one transaction
      const tx1 = this.db.transaction(STORE_NAME, "readonly");
      const store1 = tx1.objectStore(STORE_NAME);
      const pending = await store1.index("status").getAll("pending");
      await tx1.done; // Wait for first transaction to complete

      // Process each submission
      for (const submission of pending) {
        try {
       
          const payload = {
            bdmId: submission.formData.bdmId,
            attendanceId: submission.formData.attendanceId,
            action: submission.formData.action,
            latitude: submission.formData.latitude,
            longitude: submission.formData.longitude,
            bdmLeadActionId: submission.formData.bdmLeadActionId,
            ...(submission.formData.leadDetailId && { leadDetailId: submission.formData.leadDetailId }) // Optional field
          };
          
          const endpoint = this.getEndpoint(submission.type);

          // Try to submit to backend
          const response = await fetch(endpoint, {
            method: "POST",
            body: JSON.stringify(payload),
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          });

          if (response.ok) {
            // Create new transaction for deletion
            const deleteTx = this.db.transaction(STORE_NAME, "readwrite");
            const deleteStore = deleteTx.objectStore(STORE_NAME);
            await deleteStore.delete(submission.id);
            await deleteTx.done; // Wait for deletion to complete

            this.notifySuccess(submission.type);
          } else {
            throw new Error("API submission failed");
          }
        } catch (error) {
          console.error("Sync error:", error);

          // Create new transaction for update
          const updateTx = this.db.transaction(STORE_NAME, "readwrite");
          const updateStore = updateTx.objectStore(STORE_NAME);

          const updatedSubmission = {
            ...submission,
            retryCount: (submission.retryCount || 0) + 1,
            status: submission.retryCount >= 2 ? "failed" : "pending",
          };

          await updateStore.put(updatedSubmission);
          await updateTx.done; // Wait for update to complete
        }
      }
    } catch (error) {
      console.error("Sync process error:", error);
    }
  }

  async getPendingSubmissions() {
    if (!this.db) await this.initDB();
    try {
      const tx = this.db.transaction("pending-submissions", "readonly");
      const store = tx.objectStore("pending-submissions");
      const pendingItems = await store.index("status").getAll("pending");

      // Format the data to match backend structure
      return pendingItems.map((item) => ({
        ...item.formData,
        id: item.id,
        travel_details: {
          checkout_time: item.timestamp,
          syncing: true,
          type: item.type,
          checkIntime: item.checkInTime
        },
        offline: true,
      }));
    } catch (error) {
      console.error("Error getting pending submissions:", error);
      return [];
    }
  }

  // Add method to check if a specific task is syncing
  async isTaskSyncing(taskId) {
    if (!this.db) await this.initDB();
    try {
      const tx = this.db.transaction("pending-submissions", "readonly");
      const store = tx.objectStore("pending-submissions");
      const task = await store.get(taskId);
      return task !== undefined;
    } catch (error) {
      console.error("Error checking task sync status:", error);
      return false;
    }
  }

  // In OfflineStorage.js
  async removePendingSubmission(id, type) {
    console.log(id);
    
    if (!this.db) await this.initDB();
    const deleteTx = this.db.transaction(STORE_NAME, "readwrite");
    const deleteStore = deleteTx.objectStore(STORE_NAME);
    await deleteStore.delete(id);
    await deleteTx.done; // Wait for deletion to complete

    this.notifySuccess(type);
  }

  async checkIfExists(id) {
    if (!this.db) await this.initDB();
    const tx = this.db.transaction("pending-submissions", "readonly");
    const submission = await tx.store.get(id);
    return submission !== undefined;
  }

  // Convert base64 back to File
  async base64ToFile(base64, filename, type) {
    const res = await fetch(base64);
    const blob = await res.blob();
    return new File([blob], filename, { type });
  }

  // Get API endpoint based on submission type
  // Get API endpoint based on submission type
  getEndpoint(type) {
    const endpoints = {
      meeting: `${BASE_URL}/create/meeting`,
      site_visit:
        `${BASE_URL}/create/sitevisit`,
      estimate:
        `${BASE_URL}/create/estimations`,
        check_in: `${BASE_URL}/bdm/checkin`,  
    };
    return endpoints[type];
  }

  // Notify success through custom event
  notifySuccess(type, details = {}) {
    console.log("here");

    const event = new CustomEvent("submission-synced", {
      detail: {
        type,
        needsRefresh: true, // This flag indicates data needs refreshing
        ...details,
        timestamp: new Date().toISOString(),
      },
    });

    window.dispatchEvent(event);
  }

  // Check if there are pending submissions
  async hasPendingSubmissions() {
    if (!this.db) await this.initDB();
    const count = await this.db
      .transaction(STORE_NAME)
      .store.index("status")
      .count("pending");
    return count > 0;
  }
}

export const offlineStorage = new OfflineStorage();
